import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useRoomsApi } from "./useRoomsApi";

export const queryKeys = {
    rooms: (propertyId) => ['rooms', { propertyId }],
    room: (propertyId, roomId) => ['room', { propertyId, roomId }],
};

/**
 * Hook for managing rooms belonging to a property.
 *
 * When roomId is provided, fetches a single room — seeding from the rooms-list
 * cache as initialData to avoid a redundant network request.
 *
 * Mutation functions keep both the rooms-list cache and the individual room
 * cache in sync so downstream queries never need to refetch.
 *
 * Room pictures are managed separately via useRoomPicturesData.
 *
 * @param {string|null} propertyId - Property identifier
 * @param {string|null} [roomId]   - Room identifier; when provided, fetches only this room
 */
export const useRoomsData = (propertyId, roomId) => {
    const { findAll, findById, createRoom, updateRoom, deleteRoom } = useRoomsApi();

    const queryClient = useQueryClient();

    const cachedRooms = () => queryClient.getQueryData(queryKeys.rooms(propertyId));

    const queryResults = useQuery({
        queryKey: roomId ? queryKeys.room(propertyId, roomId) : queryKeys.rooms(propertyId),
        queryFn: roomId ? () => findById(propertyId, roomId) : () => findAll(propertyId),
        initialData: roomId ? () => cachedRooms()?.find(r => r.id === roomId) : undefined,
        staleTime: defaultStaleTime,
        enabled: !!propertyId,
    });

    const _createRoom = async (room) => {
        const createdRoom = await createRoom(propertyId, room);

        queryClient.setQueryData(queryKeys.room(propertyId, createdRoom.id), createdRoom);

        const cached = cachedRooms();
        if (cached) {
            queryClient.setQueryData(queryKeys.rooms(propertyId), [...cached, createdRoom]);
        }

        return createdRoom;
    };

    const _updateRoom = async (room) => {
        const updatedRoom = await updateRoom(propertyId, room);

        queryClient.setQueryData(queryKeys.room(propertyId, updatedRoom.id), updatedRoom);

        const cached = cachedRooms();
        if (cached) {
            queryClient.setQueryData(
                queryKeys.rooms(propertyId),
                cached.map(r => r.id === updatedRoom.id ? updatedRoom : r)
            );
        }

        return updatedRoom;
    };

    const _deleteRoom = async (id) => {
        await deleteRoom(propertyId, id);

        queryClient.removeQueries(queryKeys.room(propertyId, id));

        const cached = cachedRooms();
        if (cached) {
            queryClient.setQueryData(queryKeys.rooms(propertyId), cached.filter(r => r.id !== id));
        }
    };

    return {
        queryResults,
        createRoom: _createRoom,
        updateRoom: _updateRoom,
        deleteRoom: _deleteRoom,
    };
};
